import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const STORAGE_KEY = 'rumio-site-wizard-state';

export interface WizardState {
  currentStep: number;
  completedSteps: number[];
  savedAt: string;
  // When a user on a later step jumps backward via the stepper, we
  // remember where they were so they can "Return to step N" with one
  // click instead of clicking Continue through every intermediate step.
  // Cleared once they reach or pass returnToStep via normal navigation.
  returnToStep: number | null;

  // Step 1: Identity & Domains
  // 'ecommerce' (default) builds a tenant store. 'saas' builds a marketing
  // site for the platform itself (e.g. rumio.world). 'museum' builds a
  // cultural-institution scaffold (Visit/Exhibits/Events/Membership/Donate
  // pages, Penny pre-enabled, gift shop opt-in). 'lodging' builds a
  // short-term rental direct-booking site (Properties/Bookings/Reservations,
  // iCal channel sync, no cart/checkout).
  siteType: 'ecommerce' | 'saas' | 'museum' | 'lodging' | 'social_casino';
  // Museum-specific wizard inputs. Ignored unless siteType === 'museum'.
  museumHasGiftShop?: boolean;
  museumCollectionFocus?: string;
  museumTaxStatus?: '501c3' | '501c6' | 'for_profit' | '';
  museumCity?: string;
  museumRegion?: string;
  // Lodging-specific wizard inputs. Ignored unless siteType === 'lodging'.
  lodgingMarketRegion?: string;
  lodgingHostType?: 'single_host' | 'property_manager';
  lodgingPropertyCount?: number;
  lodgingPaymentModel?: 'deposit_only' | 'full_upfront' | 'deposit_balance';
  /** Single-property variant — the site is one home (homepage = the property). */
  lodgingSingleProperty?: boolean;
  // Social-casino-specific wizard inputs. Ignored unless siteType === 'social_casino'.
  socialCasinoGameStyle?: string;
  socialCasinoMascotOrTheme?: string;
  socialCasinoMinAge?: number;
  socialCasinoGameAppId?: string;
  storeName: string;
  shortName: string;
  siteId: string;
  tagline: string;
  businessDescription: string;
  domainChoice: 'subdomain' | 'custom';
  frontendDomain: string;
  apiDomain: string;
  aliasDomains: string[];

  // Step 2: Branding
  palettes: any | null;
  darkOverrides: any | null;
  fontPairingId: string;
  paletteExplanation: string;
  /** Lucide icon name (from sites/lucide-icon-catalog.ts) used as the new
   *  tenant's brand glyph in image placeholders. Empty string = let the
   *  wizard backend AI pick one. */
  brandSymbol: string;

  // Step 3: Features
  features: Record<string, boolean>;
  featuresReasoning: string;

  // Step 4: Content & Copy
  heroTitle: string;
  heroSubtitle: string;
  heroCTA: string;
  aboutMission: string;
  aboutDifferentiator: string;
  aboutAudience: string;
  valuePropositions: { icon: string; title: string; description: string }[];
  contactEmail: string;
  socialLinks: { platform: string; url: string }[];
  // Nameservers returned by Cloudflare email-routing setup that the user
  // must copy into their registrar before advancing past Step 4.
  pendingNameservers: string[];
  copiedNameservers: string[];

  // Step 5: Content Generation
  generateContent: boolean;
  courseDescriptions: string;
  productCount: number;
  blogPostCount: number;

  // Step 6: Integrations
  stripeSecretKey: string;
  stripePublishableKey: string;
  stripeWebhookSecret: string;
  setupAxiss: boolean;
  /**
   * Provision an AI phone line (Axiss Support + Voice). Opt-in, unlike
   * setupAxiss: a number is a recurring cost and a published contact point.
   * Requires setupAxiss — voice answers for an Axiss tenant.
   */
  setupVoice: boolean;
  voiceNumberType: 'toll_free' | 'local';
  /** Preferred area code: exact on local, a prefix wish on toll-free. */
  voiceAreaCode: string;
  setupTaxEngine: boolean;
  additionalAdmins: string[];

  // Who owns the new tenant. 'platform' (default) = me / Rumio. 'customer' =
  // external customer; wizard skips seeding the platform owner into the
  // tenant DB + Axiss member list so I don't appear in their admin user list.
  // Platform owner retains super-admin access via JWT-email identity.
  ownership: 'platform' | 'customer';
}

interface WizardActions {
  updateState: (partial: Partial<WizardState>) => void;
  resetState: () => void;
}

const initialState: WizardState = {
  currentStep: 0,
  completedSteps: [],
  savedAt: '',
  returnToStep: null,

  // Step 1
  siteType: 'ecommerce',
  storeName: '',
  shortName: '',
  siteId: '',
  tagline: '',
  businessDescription: '',
  domainChoice: 'subdomain',
  frontendDomain: '',
  aliasDomains: [],
  apiDomain: '',

  // Step 2
  palettes: null,
  darkOverrides: null,
  fontPairingId: 'inter-playfair',
  paletteExplanation: '',
  brandSymbol: '',

  // Step 3
  features: {},
  featuresReasoning: '',

  // Step 4
  heroTitle: '',
  heroSubtitle: '',
  heroCTA: '',
  aboutMission: '',
  aboutDifferentiator: '',
  aboutAudience: '',
  valuePropositions: [],
  contactEmail: '',
  socialLinks: [],
  pendingNameservers: [],
  copiedNameservers: [],

  // Step 5
  generateContent: true,
  courseDescriptions: '',
  productCount: 8,
  blogPostCount: 3,

  // Step 6
  stripeSecretKey: '',
  stripePublishableKey: '',
  stripeWebhookSecret: '',
  setupAxiss: true,
  setupVoice: false,
  voiceNumberType: 'toll_free',
  voiceAreaCode: '',
  setupTaxEngine: true,
  additionalAdmins: [],

  ownership: 'platform',
};

export const useWizardState = create<WizardState & WizardActions>()(
  persist(
    (set) => ({
      ...initialState,

      updateState: (partial) =>
        set((state) => ({
          ...state,
          ...partial,
          savedAt: new Date().toISOString(),
        })),

      resetState: () => {
        set({ ...initialState, savedAt: '' });
        // Also remove from localStorage directly as a safeguard
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          // Ignore storage errors
        }
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => {
        // Persist everything except the actions
        const { updateState: _, resetState: __, ...data } = state;
        return data;
      },
    }
  )
);

/**
 * Returns true if there is a resumable wizard draft in localStorage.
 * Checks for a non-empty savedAt timestamp, meaning the user has
 * previously started filling out the wizard.
 */
export function hasResumableState(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    const state = parsed?.state;
    return !!(state?.savedAt && state.savedAt !== '');
  } catch {
    return false;
  }
}
